import { useCallback, useEffect } from "react";
import { useRecoilState } from "recoil";
import { ubicacionEstado, userCoords } from "recoilState/GlobalState";

/**
 * Por qué no hay ubicación. Cada uno pide un mensaje distinto, y sobre todo: en dos de
 * ellos el botón de "reintentar" NO puede funcionar, así que ni se ofrece.
 */
export const ESTADO_UBICACION = {
  /** Todavía no se resolvió. */
  CARGANDO: "cargando",
  OK: "ok",
  /** Nunca decidió, o cerró el cartel sin elegir. Reintentar vuelve a mostrarlo. */
  PENDIENTE: "pendiente",
  /** Bloqueada ("no permitir nunca"). Reintentar NO muestra el cartel de vuelta. */
  BLOQUEADA: "bloqueada",
  /** La página no está en HTTPS. No es culpa del usuario y no hay nada que reintentar. */
  NO_SEGURO: "noSeguro",
  /** El permiso está dado pero el dispositivo no pudo ubicarse. Reintentar sirve. */
  ERROR: "error",
};

/** Lee el estado del permiso sin pedirlo. `null` si el navegador no lo sabe decir. */
const consultarPermiso = async () => {
  try {
    // Safari no soporta consultar `geolocation` en la Permissions API: rechaza.
    const permiso = await navigator.permissions?.query({ name: "geolocation" });
    return permiso ?? null;
  } catch {
    return null;
  }
};

/**
 * Ubicación del usuario, con el motivo cuando no se puede obtener.
 *
 * El acceso por QR la necesita sí o sí: el login manda lat/lng para validar la sesión.
 * Antes, si el permiso fallaba, el único rastro era un `console.error` y la pantalla de
 * login se quedaba mostrando "escaneá el QR" sin ningún QR debajo.
 *
 * EL ESTADO VIVE EN RECOIL, no en el hook. Así lo pueden usar dos componentes a la vez
 * —`App` lo pide al arrancar y el login ofrece reintentar— sin duplicar la petición ni
 * quedar cada uno con su copia.
 *
 * @param pedirAlMontar solo lo pasa el dueño (App). El resto lee y puede reintentar.
 */
export function useUbicacion({ pedirAlMontar = false } = {}) {
  const [coords, setCoords] = useRecoilState(userCoords);
  const [estado, setEstado] = useRecoilState(ubicacionEstado);

  const pedir = useCallback(() => {
    // La API de geolocalización exige contexto seguro. Sobre HTTP falla con el MISMO
    // código que una denegación del usuario, así que sin este chequeo le pediríamos
    // que desbloquee un permiso que nunca bloqueó.
    if (!window.isSecureContext) {
      setEstado(ESTADO_UBICACION.NO_SEGURO);
      return;
    }

    setEstado(ESTADO_UBICACION.CARGANDO);
    navigator.geolocation.getCurrentPosition(
      (posicion) => {
        setCoords({
          lat: posicion.coords.latitude,
          lng: posicion.coords.longitude,
        });
        setEstado(ESTADO_UBICACION.OK);
      },
      async (error) => {
        // Un código 1 tapa dos casos distintos: cerrar el cartel (el permiso sigue
        // en "prompt" y se puede volver a pedir) y bloquearlo (ya no vuelve a
        // aparecer). Solo la Permissions API los separa.
        if (error.code !== error.PERMISSION_DENIED) {
          setEstado(ESTADO_UBICACION.ERROR);
          return;
        }
        const permiso = await consultarPermiso();
        setEstado(
          permiso?.state === "denied"
            ? ESTADO_UBICACION.BLOQUEADA
            : ESTADO_UBICACION.PENDIENTE
        );
      }
    );
  }, [setCoords, setEstado]);

  useEffect(() => {
    if (!pedirAlMontar) return undefined;
    pedir();

    // Si el usuario desbloquea desde la configuración del navegador, `onchange`
    // avisa y se vuelve a pedir sola: no hace falta que recargue la página. Es lo
    // único que saca del paso al que eligió "no permitir nunca".
    let permiso;
    let vigente = true;
    const alCambiar = () => {
      if (permiso.state === "granted") pedir();
      else if (permiso.state === "denied") setEstado(ESTADO_UBICACION.BLOQUEADA);
    };

    (async () => {
      permiso = await consultarPermiso();
      if (!permiso || !vigente) return;
      permiso.addEventListener("change", alCambiar);
    })();

    return () => {
      vigente = false;
      permiso?.removeEventListener("change", alCambiar);
    };
  }, [pedirAlMontar, pedir, setEstado]);

  return { coords, estado, reintentar: pedir };
}
